// FILE: client/src/screens/admin/AdminSubcategoryListScreen.jsx

import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Badge, Button, Col, Form, Image, Row, Table } from "react-bootstrap";

import Loader from "../../components/Loader";
import Message from "../../components/Message";
import { CATALOGS, isValidCatalogKey } from "../../constants/shopMeta";
import { adminListCategories } from "../../actions/categoryActions";
import {
  adminDeleteSubcategory,
  adminListSubcategories,
  resetAdminSubcategoryDelete,
} from "../../actions/subcategoryActions";

const DEFAULT_SHOP_KEY = CATALOGS?.[0]?.shopKey || "haberdashery";

const getEntityId = (entity) => {
  if (!entity) return "";

  if (typeof entity === "string") return entity;

  return String(entity._id || entity.id || "");
};

const getCoverImageUrl = (entity) => {
  const firstImage = Array.isArray(entity?.images) ? entity.images[0] : null;

  if (!firstImage) return "";

  if (typeof firstImage === "string") return firstImage;

  return firstImage.url || "";
};

const formatDate = (value) => {
  if (!value) return "-";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return "-";

  return date.toLocaleDateString();
};

const sortByName = (items = []) =>
  [...items].sort((a, b) =>
    String(a?.name || "").localeCompare(String(b?.name || "")),
  );

const AdminSubcategoryListScreen = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { shopKey: routeShopKey } = useParams();

  const [parentId, setParentId] = useState("");

  const selectedShopKey = useMemo(() => {
    const safeRouteShopKey = String(routeShopKey || "").trim();

    return isValidCatalogKey(safeRouteShopKey)
      ? safeRouteShopKey
      : DEFAULT_SHOP_KEY;
  }, [routeShopKey]);

  const adminSubcategoryListState = useSelector(
    (state) => state.adminSubcategoryList || {},
  );

  const {
    loading,
    error,
    subcategories: rawSubcategories = [],
  } = adminSubcategoryListState;

  const subcategories = Array.isArray(rawSubcategories) ? rawSubcategories : [];

  const adminSubcategoryDeleteState = useSelector(
    (state) => state.adminSubcategoryDelete || {},
  );

  const {
    loading: loadingDelete,
    error: errorDelete,
    success: successDelete,
  } = adminSubcategoryDeleteState;

  const adminCategoryListState = useSelector(
    (state) => state.adminCategoryList || {},
  );

  const {
    loading: loadingCategories,
    error: errorCategories,
    categories: rawCategories = [],
  } = adminCategoryListState;

  const categories = sortByName(
    Array.isArray(rawCategories) ? rawCategories : [],
  );

  useEffect(() => {
    if (!isValidCatalogKey(routeShopKey)) {
      navigate(`/admin/shop/${DEFAULT_SHOP_KEY}/subcategories`, {
        replace: true,
      });
    }
  }, [navigate, routeShopKey]);

  useEffect(() => {
    setParentId("");
  }, [selectedShopKey]);

  useEffect(() => {
    if (!isValidCatalogKey(selectedShopKey)) return;

    dispatch(
      adminListCategories({
        shopKey: selectedShopKey,
        pageNumber: 1,
        pageSize: 200,
      }),
    );
  }, [dispatch, selectedShopKey]);

  useEffect(() => {
    if (!isValidCatalogKey(selectedShopKey)) return;

    dispatch(
      adminListSubcategories({
        shopKey: selectedShopKey,
        parentId,
        pageNumber: 1,
        pageSize: 200,
      }),
    );
  }, [dispatch, selectedShopKey, parentId]);

  useEffect(() => {
    if (!successDelete) return;

    dispatch(resetAdminSubcategoryDelete());

    dispatch(
      adminListSubcategories({
        shopKey: selectedShopKey,
        parentId,
        pageNumber: 1,
        pageSize: 200,
      }),
    );
  }, [dispatch, selectedShopKey, parentId, successDelete]);

  const handleShopChange = (event) => {
    const nextShopKey = event.target.value;

    if (!isValidCatalogKey(nextShopKey)) return;

    setParentId("");
    navigate(`/admin/shop/${nextShopKey}/subcategories`);
  };

  const deleteHandler = (subcategory) => {
    const subcategoryId = getEntityId(subcategory);

    if (!subcategoryId) {
      window.alert("Cannot delete this subcategory because its id is missing.");
      return;
    }

    const confirmed = window.confirm(
      `Delete subcategory "${subcategory?.name || "Untitled"}"?`,
    );

    if (!confirmed) return;

    dispatch(adminDeleteSubcategory(subcategoryId));
  };

  return (
    <>
      <Row className="align-items-end mb-3 gy-3">
        <Col md>
          <h1 className="mb-1">Subcategories</h1>
          <div className="text-muted">Manage subcategories by catalogue.</div>
        </Col>

        <Col md={4} lg={3}>
          <Form.Group controlId="shopSelector">
            <Form.Label className="fw-semibold">Catalogue</Form.Label>

            <Form.Select
              value={selectedShopKey}
              onChange={handleShopChange}
              disabled={loading || loadingDelete}
            >
              {CATALOGS.map((shop) => (
                <option key={shop.shopKey} value={shop.shopKey}>
                  {shop.name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>

        <Col xs="auto">
          <Button
            as={Link}
            to={`/admin/shop/${selectedShopKey}/subcategories/create`}
            disabled={!isValidCatalogKey(selectedShopKey)}
          >
            Create Subcategory
          </Button>
        </Col>
      </Row>

      <Row className="mb-3">
        <Col md={5} lg={4}>
          <Form.Group controlId="parentFilter">
            <Form.Label>Filter by Category</Form.Label>

            <Form.Select
              value={parentId}
              onChange={(event) => setParentId(event.target.value)}
              disabled={loadingCategories || loading}
            >
              <option value="">
                {loadingCategories ? "Loading categories..." : "All Categories"}
              </option>

              {categories.map((category) => {
                const categoryId = getEntityId(category);

                return (
                  <option key={categoryId} value={categoryId}>
                    {category.name}
                    {category.isPublished === false ? " (Draft)" : ""}
                  </option>
                );
              })}
            </Form.Select>
          </Form.Group>
        </Col>
      </Row>

      {loadingDelete ? <Loader /> : null}

      {errorDelete ? <Message variant="danger">{errorDelete}</Message> : null}

      {errorCategories ? (
        <Message variant="danger">{errorCategories}</Message>
      ) : null}

      {loading ? (
        <Loader />
      ) : error ? (
        <Message variant="danger">{error}</Message>
      ) : subcategories.length === 0 ? (
        <Message>No subcategories found for this shop.</Message>
      ) : (
        <Table striped bordered hover responsive className="align-middle">
          <thead>
            <tr>
              <th style={{ width: 72 }}>IMAGE</th>
              <th>NAME</th>
              <th>SLUG</th>
              <th>PARENT CATEGORY</th>
              <th>PUBLISHED</th>
              <th>CREATED</th>
              <th className="text-end">ACTIONS</th>
            </tr>
          </thead>

          <tbody>
            {subcategories.map((subcategory) => {
              const subcategoryId = getEntityId(subcategory);
              const coverImageUrl = getCoverImageUrl(subcategory);

              return (
                <tr key={subcategoryId}>
                  <td>
                    {coverImageUrl ? (
                      <Image
                        src={coverImageUrl}
                        alt={subcategory.name || "Subcategory image"}
                        rounded
                        style={{
                          width: 48,
                          height: 48,
                          objectFit: "cover",
                        }}
                      />
                    ) : (
                      <span className="text-muted">—</span>
                    )}
                  </td>

                  <td>{subcategory.name}</td>

                  <td>
                    <code>{subcategory.slug}</code>
                  </td>

                  <td>{subcategory.parentId?.name || "-"}</td>

                  <td>
                    {subcategory.isPublished ? (
                      <Badge bg="success">Published</Badge>
                    ) : (
                      <Badge bg="secondary">Draft</Badge>
                    )}
                  </td>

                  <td>{formatDate(subcategory.createdAt)}</td>

                  <td className="text-end text-nowrap">
                    <Button
                      as={Link}
                      to={`/admin/shop/${selectedShopKey}/subcategories/${subcategoryId}/edit`}
                      variant="light"
                      size="sm"
                      className="me-2"
                    >
                      Edit
                    </Button>

                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() => deleteHandler(subcategory)}
                      disabled={loadingDelete}
                    >
                      Delete
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      )}
    </>
  );
};

export default AdminSubcategoryListScreen;
